import React from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import { MetricVersionTag } from "@/metrics/metric-display"
import { getCompositeAgreementRate } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"
import { Checkbox, Tooltip } from "@coval/ui"
import { AlertTriangle, Copy } from "lucide-react"

import { handleAccessibleRowKeyDown } from "@/utils/accessibleRowKeyDown"
import { cn } from "@/utils/cn"

import { isValidCriterionResult, type CriterionResult } from "../../types"
import {
  getHumanReviewValue,
  type GroupedSimulationData,
  type HumanReviewData,
} from "../../utils/filterSimulations"
import { useTestMetricRuntime } from "../contexts"
import { HumanReviewAnnotationCard } from "./HumanReviewAnnotationCard"

export const CRITERIA_VIEW_MODE = {
  SCORE: "score",
  CRITERIA: "criteria",
} as const

export type CriteriaViewMode =
  (typeof CRITERIA_VIEW_MODE)[keyof typeof CRITERIA_VIEW_MODE]

function getHumanReviewCardDisplayValue(
  review: HumanReviewData,
  aiOutput: GroupedSimulationData["rawMetricOutput"],
  metric: MetricRecord | null,
  showAgreementRate: boolean,
  mode: CriteriaViewMode,
): string | null {
  if (showAgreementRate) {
    if (!aiOutput) {
      return null
    }
    const rate = getCompositeAgreementRate(
      aiOutput,
      review,
      metric?.metric_metadata,
      mode,
    )
    return rate == null ? null : `${Math.round(rate)}%`
  }
  return metric
    ? metric.getHumanReviewDisplayValue(review)
    : getHumanReviewValue(review)
}

interface HumanCriteriaCellProps {
  reviews: HumanReviewData[]
  aiCriteria: CriterionResult[]
  aiOutput: GroupedSimulationData["rawMetricOutput"]
  showAgreementRate: boolean
  conflictIndices: Set<number>
  isExpanded: boolean
  onToggleExpand: () => void
  onSetPrimary: (reviewId: string) => void
  isSettingPrimary: boolean
  metric: MetricRecord | null
}

const HumanCriteriaCell: React.FC<HumanCriteriaCellProps> = ({
  reviews,
  aiCriteria,
  aiOutput,
  showAgreementRate,
  conflictIndices,
  isExpanded,
  onToggleExpand,
  onSetPrimary,
  isSettingPrimary,
  metric,
}) => {
  if (reviews.length === 0) {
    return <span className="text-xs text-coval-text-muted">N/A</span>
  }

  const countedReview = reviews.find((r) => r.isCountedAsPrimary) ?? reviews[0]
  const countedCriteria = buildHumanCriteria(countedReview, aiCriteria)
  const additionalCount = reviews.length - 1

  // Check if reviewers disagree with each other
  const hasReviewerDisagreement =
    metric &&
    reviews.length > 1 &&
    reviews.some((r) => {
      const reviewValue = {
        float_value: r.float_value,
        string_value: r.string_value,
        set_value: r.set_value,
      }
      const countedValue = {
        float_value: countedReview.float_value,
        string_value: countedReview.string_value,
        set_value: countedReview.set_value,
      }
      return !metric.compareValues(countedValue, reviewValue)
    })

  if (!isExpanded) {
    return (
      <div className="flex flex-col gap-1">
        <HumanReviewAnnotationCard
          displayValue={getHumanReviewCardDisplayValue(
            countedReview,
            aiOutput,
            metric,
            showAgreementRate,
            CRITERIA_VIEW_MODE.CRITERIA,
          )}
          targetCondition={
            showAgreementRate
              ? undefined
              : metric?.metric_metadata?.target_condition
          }
          assignee={countedReview.reviewer}
          date={countedReview.date}
          isPrimary={countedReview.isCountedAsPrimary}
          canSetPrimary={false}
          criteria={countedCriteria}
          conflictIndices={conflictIndices}
        />
        {(additionalCount > 0 || hasReviewerDisagreement) && (
          <div className="flex items-center gap-2">
            {additionalCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleExpand()
                }}
                className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors"
                aria-label="Show other annotations"
              >
                +{additionalCount} more
              </button>
            )}
            {hasReviewerDisagreement && (
              <Tooltip content="Reviewers disagree">
                <AlertTriangle className="w-3.5 h-3.5 text-coval-danger-text" />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    )
  }

  // Expanded view — show all reviewers' criteria
  return (
    <div className="flex flex-col gap-2">
      {reviews.map((review) => (
        <div
          role="presentation"
          key={review.id}
          onClick={(e) => e.stopPropagation()}
        >
          <HumanReviewAnnotationCard
            displayValue={getHumanReviewCardDisplayValue(
              review,
              aiOutput,
              metric,
              showAgreementRate,
              CRITERIA_VIEW_MODE.CRITERIA,
            )}
            targetCondition={
              showAgreementRate
                ? undefined
                : metric?.metric_metadata?.target_condition
            }
            assignee={review.reviewer}
            date={review.date}
            isPrimary={review.isCountedAsPrimary}
            canSetPrimary={reviews.length > 1}
            onSetPrimary={() => onSetPrimary(review.id)}
            isSettingPrimary={isSettingPrimary}
            criteria={buildHumanCriteria(review, aiCriteria)}
            conflictIndices={
              review.id === countedReview.id ? conflictIndices : undefined
            }
          />
        </div>
      ))}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onToggleExpand()
        }}
        className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors text-left"
        aria-label="Collapse annotations"
      >
        Show less
      </button>
    </div>
  )
}

/**
 * Build a CriterionResult[] from a human review's set_value by mapping
 * positionally to the AI output's individual_results for criterion names.
 */
export const buildHumanCriteria = (
  review: HumanReviewData | undefined,
  aiCriteria: CriterionResult[],
): CriterionResult[] => {
  const setValue = review?.set_value
  if (!setValue || setValue.length === 0) {
    return []
  }
  return setValue.map((result, i) => ({
    criterion: aiCriteria[i]?.criterion ?? `Criterion ${i + 1}`,
    result: isValidCriterionResult(result) ? result : CRITERION_VALUE.UNKNOWN,
    explanation: "",
  }))
}

/**
 * Returns the set of criterion indices where AI and human disagree.
 */
export const getCriteriaConflictIndices = (
  aiCriteria: CriterionResult[],
  humanCriteria: CriterionResult[],
): Set<number> => {
  const conflicts = new Set<number>()
  if (aiCriteria.length === 0 || humanCriteria.length === 0) {
    return conflicts
  }
  const len = Math.min(aiCriteria.length, humanCriteria.length)
  for (let i = 0; i < len; i++) {
    // Only flag conflict when human provided a definite answer (not UNKNOWN/empty)
    const h = humanCriteria[i].result
    if (h && h !== CRITERION_VALUE.UNKNOWN && aiCriteria[i].result !== h) {
      conflicts.add(i)
    }
  }
  return conflicts
}

// Extended GroupedSimulationData with additional fields for the table
export interface ExtendedGroupedSimulationData extends GroupedSimulationData {
  aiReview: {
    value: string | null
    explanation: string | null
    metadata: string | null
  }
  aiCriteria: CriterionResult[]
  mostRecentExperiment: {
    value: string | null
    rawFloatValue?: number | null
    criteriaResults?: CriterionResult[]
    explanation: string | null
    errorStatus?: string
    errorMessage?: string
  }
  isStaleVersion: boolean
  versionStatus: {
    ranVersion: number
    currentVersion: number
    isStale: boolean
  } | null
}

interface HumanReviewCellProps {
  reviews: HumanReviewData[]
  aiOutput: GroupedSimulationData["rawMetricOutput"]
  showAgreementRate: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onSetPrimary: (reviewId: string) => void
  isSettingPrimary: boolean
  metric: MetricRecord | null
}

const HumanReviewCell: React.FC<HumanReviewCellProps> = ({
  reviews,
  aiOutput,
  showAgreementRate,
  isExpanded,
  onToggleExpand,
  onSetPrimary,
  isSettingPrimary,
  metric,
}) => {
  if (reviews.length === 0) {
    return (
      <div className="flex items-start gap-2">
        <span className="text-sm text-coval-text">No annotation</span>
      </div>
    )
  }

  // Find the review that's counted as primary
  const countedReview = reviews.find((r) => r.isCountedAsPrimary) || reviews[0]
  const additionalCount = reviews.length - 1

  // Check if reviews disagree (have different values) using MetricRecord comparison
  const countedReviewValue = {
    float_value: countedReview.float_value,
    string_value: countedReview.string_value,
    set_value: countedReview.set_value,
  }
  const hasDisagreement =
    metric &&
    reviews.length > 1 &&
    reviews.some((r) => {
      const reviewValue = {
        float_value: r.float_value,
        string_value: r.string_value,
        set_value: r.set_value,
      }
      return !metric.compareValues(countedReviewValue, reviewValue)
    })

  const countedDisplayValue = getHumanReviewCardDisplayValue(
    countedReview,
    aiOutput,
    metric,
    showAgreementRate,
    CRITERIA_VIEW_MODE.SCORE,
  )

  if (!isExpanded) {
    return (
      <div className="flex flex-col gap-1">
        <HumanReviewAnnotationCard
          displayValue={countedDisplayValue}
          targetCondition={
            showAgreementRate
              ? undefined
              : metric?.metric_metadata?.target_condition
          }
          assignee={countedReview.reviewer}
          date={countedReview.date}
          isPrimary={countedReview.isCountedAsPrimary}
          canSetPrimary={false}
          explanation={countedReview.explanation}
        />
        {(additionalCount > 0 || hasDisagreement) && (
          <div className="flex items-center gap-2">
            {additionalCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleExpand()
                }}
                className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors"
                aria-label={`Show ${additionalCount} more reviews`}
              >
                +{additionalCount} more
              </button>
            )}
            {hasDisagreement && (
              <Tooltip content="Reviewers disagree">
                <AlertTriangle className="w-3.5 h-3.5 text-coval-danger-text" />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    )
  }

  // Expanded view - show all reviews with ability to set primary
  return (
    <div className="flex flex-col gap-3">
      {reviews.map((review) => {
        const displayValue = getHumanReviewCardDisplayValue(
          review,
          aiOutput,
          metric,
          showAgreementRate,
          CRITERIA_VIEW_MODE.SCORE,
        )
        return (
          // stopPropagation prevents the table row click handler from firing
          <div
            role="presentation"
            key={review.id}
            onClick={(e) => e.stopPropagation()}
          >
            <HumanReviewAnnotationCard
              displayValue={displayValue}
              targetCondition={
                showAgreementRate
                  ? undefined
                  : metric?.metric_metadata?.target_condition
              }
              assignee={review.reviewer}
              date={review.date}
              isPrimary={review.isCountedAsPrimary}
              canSetPrimary={reviews.length > 1}
              onSetPrimary={() => onSetPrimary(review.id)}
              isSettingPrimary={isSettingPrimary}
              explanation={review.explanation}
            />
          </div>
        )
      })}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onToggleExpand()
        }}
        className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors text-left"
        aria-label="Show fewer reviews"
      >
        Show less
      </button>
    </div>
  )
}

interface SimulationRowProps {
  data: ExtendedGroupedSimulationData
  isEvaluating?: boolean
  isExpanded: boolean
  isActive: boolean
  isSelected: boolean
  disableSelect: boolean
  onToggleSelect: () => void
  onToggleExpand: () => void
  onRowClick: (simulationOutputId: string) => void
  onPointerEnter: (simulationOutputId: string) => void
  onPointerLeave: (simulationOutputId: string) => void
  hasConflict: boolean
  onSetPrimary: (reviewId: string) => void
  isSettingPrimary: boolean
  canRunEvaluations: boolean
  metric: MetricRecord | null
  criteriaViewMode: CriteriaViewMode
  showHumanAgreementRate: boolean
}

export const SimulationRow: React.FC<SimulationRowProps> = ({
  data,
  isEvaluating,
  isExpanded,
  isActive,
  isSelected,
  disableSelect,
  onToggleSelect,
  onToggleExpand,
  onRowClick,
  onPointerEnter,
  onPointerLeave,
  hasConflict,
  onSetPrimary,
  isSettingPrimary,
  canRunEvaluations,
  metric,
  criteriaViewMode,
  showHumanAgreementRate,
}) => {
  const { includeOldVersions } = useTestMetricRuntime()
  const isGreyedOut = data.isStaleVersion && !includeOldVersions
  const handleCopyId = () => {
    void navigator.clipboard.writeText(data.simulationOutputId)
  }

  const isCriteriaMode = criteriaViewMode === CRITERIA_VIEW_MODE.CRITERIA
  const countedReview =
    data.humanReviews.find((r) => r.isCountedAsPrimary) ?? data.humanReviews[0]
  const humanCriteria = isCriteriaMode
    ? buildHumanCriteria(countedReview, data.aiCriteria)
    : []
  const conflictIndices =
    isCriteriaMode && hasConflict
      ? getCriteriaConflictIndices(data.aiCriteria, humanCriteria)
      : new Set<number>()
  const experimentConflictIndices =
    isCriteriaMode && data.mostRecentExperiment.criteriaResults?.length
      ? getCriteriaConflictIndices(
          data.mostRecentExperiment.criteriaResults,
          humanCriteria,
        )
      : new Set<number>()

  return (
    <tr
      className={cn(
        "cursor-pointer transition-colors hover:bg-surface-1/50 [&>td]:border-b [&>td]:border-coval-border",
        hasConflict && "bg-coval-danger-surface",
        isActive && !hasConflict && "bg-surface-1/50",
      )}
      data-simulation-review-id={data.simulationOutputId}
      tabIndex={0}
      onClick={() => {
        onRowClick(data.simulationOutputId)
      }}
      onKeyDown={(event) =>
        handleAccessibleRowKeyDown(event, () =>
          onRowClick(data.simulationOutputId),
        )
      }
      onMouseEnter={() => onPointerEnter(data.simulationOutputId)}
      onMouseLeave={() => onPointerLeave(data.simulationOutputId)}
    >
      {/* Run selection */}
      {canRunEvaluations && (
        <td
          className="px-4 py-4 text-left"
          style={{ verticalAlign: "top" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            disabled={disableSelect}
            onCheckedChange={onToggleSelect}
            aria-label={`Select conversation ${data.simulationOutputId.slice(0, 5)} for testing`}
          />
        </td>
      )}

      {/* Sim ID */}
      <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-coval-text-muted font-mono">
            {data.simulationOutputId.slice(0, 5)}
          </span>
          <Tooltip content="Copy ID">
            <button
              aria-label="Copy simulation ID"
              onClick={(e) => {
                e.stopPropagation()
                handleCopyId()
              }}
              className="text-coval-text-muted hover:text-coval-text-muted transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
      </td>

      {/* AI Review */}
      <td
        className={`px-4 py-4 text-left ${isGreyedOut ? "opacity-50" : ""}`}
        style={{ verticalAlign: "top" }}
      >
        <HumanReviewAnnotationCard
          displayValue={data.aiReview.value}
          targetCondition={metric?.metric_metadata?.target_condition}
          date={data.aiReview.metadata}
          isPrimary={false}
          canSetPrimary={false}
          criteria={isCriteriaMode ? data.aiCriteria : []}
          conflictIndices={isCriteriaMode ? conflictIndices : undefined}
          explanation={
            !isCriteriaMode
              ? (data.aiReview.explanation ?? undefined)
              : undefined
          }
          footer={
            <MetricVersionTag
              precomputedStatus={data.versionStatus}
              variant="full"
              className="mt-1"
            />
          }
        />
      </td>

      {/* Human Review */}
      <td
        className="px-4 py-4 text-left overflow-hidden"
        style={{ verticalAlign: "top" }}
      >
        {isCriteriaMode ? (
          <HumanCriteriaCell
            reviews={data.humanReviews}
            aiCriteria={data.aiCriteria}
            aiOutput={data.rawMetricOutput}
            showAgreementRate={showHumanAgreementRate}
            conflictIndices={conflictIndices}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
            onSetPrimary={onSetPrimary}
            isSettingPrimary={isSettingPrimary}
            metric={metric}
          />
        ) : (
          <HumanReviewCell
            reviews={data.humanReviews}
            aiOutput={data.rawMetricOutput}
            showAgreementRate={showHumanAgreementRate}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
            onSetPrimary={onSetPrimary}
            isSettingPrimary={isSettingPrimary}
            metric={metric}
          />
        )}
      </td>

      {/* Most Recent Experiment - only show for testable metrics */}
      {canRunEvaluations && (
        <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
          <HumanReviewAnnotationCard
            displayValue={data.mostRecentExperiment.value}
            targetCondition={metric?.metric_metadata?.target_condition}
            isPrimary={false}
            canSetPrimary={false}
            criteria={
              isCriteriaMode
                ? (data.mostRecentExperiment.criteriaResults ?? [])
                : []
            }
            conflictIndices={
              isCriteriaMode ? experimentConflictIndices : undefined
            }
            explanation={
              !isCriteriaMode
                ? (data.mostRecentExperiment.explanation ?? undefined)
                : undefined
            }
            isLoading={isEvaluating}
            errorStatus={data.mostRecentExperiment.errorStatus}
            errorMessage={data.mostRecentExperiment.errorMessage}
          />
        </td>
      )}
    </tr>
  )
}
